/**
 * PB-FOUNDATION addons plugin — stepActive listener, visible list, skip-before-paint.
 * PB-FND-7: must not call queueStepNavigation / moveStep loops from render.
 * PB-CART-1: skip render on child-line cart_edit.
 */

import { CART_EDIT_FLOW, shouldSkipPluginOnChildCartEdit } from '@theme/pb-cart-child-edit';
import { buildAddonsHtml, buildAddonRowsHtml, bindAddonsListHandlers } from '@theme/pb-addons-render';

/** FNV-1a 32-bit hash for addon list HTML — skip innerHTML when unchanged. */
export function addonsContentHash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

export function isAddonsPluginEnabled(data) {
  const source = data || (typeof window !== 'undefined' ? window.BUILDER_DATA : null);
  return !!(source && source.navigationKernel === true);
}

/**
 * @param {object} deps
 * @param {() => boolean} deps.hasAnyAddons
 * @param {() => string} deps.buildAddonsHtml
 * @param {() => void} [deps.bindAddonsListHandlers]
 * @param {(direction: number) => boolean} deps.requestSkipEmptyAddon
 * @param {() => HTMLElement | null} [deps.getContainer]
 * @param {(err: Error) => void} [deps.onRenderError]
 */
export function createAddonsPlugin(deps) {
  function getContainer() {
    if (typeof deps.getContainer === 'function') return deps.getContainer();
    if (typeof document !== 'undefined') return document.getElementById('addons-list-container');
    return null;
  }

  function requestSkip(direction) {
    if (typeof deps.requestSkipEmptyAddon === 'function') {
      return deps.requestSkipEmptyAddon(direction === -1 ? -1 : 1);
    }
    return false;
  }

  function render() {
    if (shouldSkipPluginOnChildCartEdit(CART_EDIT_FLOW, null)) {
      const container = getContainer();
      if (container) container.innerHTML = '';
      return;
    }

    if (!deps.hasAnyAddons()) {
      const container = getContainer();
      if (container) container.innerHTML = '';
      requestSkip(1);
      return;
    }

    const container = getContainer();
    if (!container) return;

    try {
      const newHtml = deps.buildAddonsHtml();
      if (!newHtml) {
        container.innerHTML = '';
        requestSkip(1);
        return;
      }

      const newHash = addonsContentHash(newHtml);
      const prevHash = container.dataset.pbAddonsContentHash || '';
      if (prevHash !== '' && newHash === prevHash) {
        return;
      }

      container.dataset.pbAddonsContentHash = newHash;
      container.innerHTML = newHtml;
      if (typeof deps.bindAddonsListHandlers === 'function') {
        deps.bindAddonsListHandlers();
      }
    } catch (err) {
      if (typeof deps.onRenderError === 'function') {
        deps.onRenderError(err);
      } else if (typeof console !== 'undefined' && console.error) {
        console.error('[PB addons plugin]', err);
      }
      container.innerHTML = '';
    }
  }

  function onStepActive(detail) {
    if (!detail || detail.kind !== 'addon') return;
    const ctx = detail.context;
    if (!ctx || !ctx.isMain) return;
    if (shouldSkipPluginOnChildCartEdit(CART_EDIT_FLOW, ctx)) return;
    render();
  }

  function wire(lifecycle) {
    if (!lifecycle || typeof lifecycle.on !== 'function') return () => {};
    return lifecycle.on('stepActive', onStepActive);
  }

  return {
    render,
    onStepActive,
    wire,
  };
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBAddons = {
    addonsContentHash,
    isAddonsPluginEnabled,
    buildAddonsHtml,
    buildAddonRowsHtml,
    bindAddonsListHandlers,
    createAddonsPlugin,
  };

  const pb = window.productBuilder;
  if (!pb || typeof pb._wireLifecycleStepPlugins !== 'function') return;
  pb._wireLifecycleStepPlugins();
  const container = document.getElementById('addons-list-container');
  const needsRender =
    container &&
    !container.innerHTML.trim();
  if (needsRender && typeof pb._emitStepActive === 'function') {
    pb._emitStepActive();
  }
}

attachToWindow();
